import { EyeOutlined } from '@ant-design/icons';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { PagingType, UTable } from '@components/common/UTable/UTable';
import { message } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useGetOrganizationsTreeQuery } from '@/api/organizations/api';
import { useGetRolesQuery } from '@/api/roles/api';
import { RoleResponse } from '@/api/roles/types';
import { useCreateUserMutation, useGetUsersQuery } from '@/api/users/api';
import { filterSchema } from '@/pages/Administration/components/users/filters.config';
import { createModalConfig } from '@/pages/Administration/components/users/modal.config';
import { Sort } from '@/shared/api/types';
import { administration } from '@/shared/security/permissions/administration';
import { mapTableIndexWithPage } from '@/utils/utils';

import { columns } from './columns.config';

export const UsersTable = () => {
  const [pageable, setPageable] = useState<PagingType>({ page: 0, size: 10 });
  const [sort, setSort] = useState<Sort[] | undefined>(undefined);
  const [filters, setFilters] = useState({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [createUser, { isLoading: isUserSaving }] = useCreateUserMutation();

  const navigate = useNavigate();

  const { data, isLoading } = useGetUsersQuery({ pageable: pageable, filters, sort });
  const { data: allOrganizations, isLoading: isOrganizationsLoading } =
    useGetOrganizationsTreeQuery();

  const { data: allRoles, isLoading: isRolesLoading } = useGetRolesQuery({});

  const onUserCreate = async (payload: any) => {
    await createUser(payload).unwrap();
    setIsCreateModalOpen(false);
    message.success('Пользователь успешно сохранен');
  };

  const onSortChange = (sort?: Sort[]) => {
    setSort(sort);
  };

  return (
    <>
      <PageTitle title={'Пользователи'} />
      <Modal
        mode={'form'}
        schema={createModalConfig(allOrganizations, allRoles as RoleResponse[])}
        title={'Добавить пользователя'}
        open={isCreateModalOpen}
        onSubmit={(payload) => {
          void onUserCreate(payload);
        }}
        onCancel={() => {
          setIsCreateModalOpen(false);
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        formKey={'createForm'}
        confirmLoading={isOrganizationsLoading || isUserSaving}
      />
      <UTable
        size={'small'}
        data={mapTableIndexWithPage(data?.content, pageable)}
        columns={columns(allRoles as RoleResponse[])}
        loading={isLoading || isRolesLoading}
        paging={pageable}
        onPagingChange={setPageable}
        onChange={onSortChange}
        currentSort={sort}
        total={data?.totalElements}
        filter={{
          filters,
          schema: filterSchema(allRoles as RoleResponse[], allOrganizations),
          onFilter: setFilters,
          onReset: () => setFilters({}),
          security: {
            permissions: administration.users.tableFiltering,
          },
        }}
        buttons={[
          {
            addBtn: true,
            action: () => setIsCreateModalOpen(true),
            security: { permissions: administration.users.registration },
          },
        ]}
        actions={() => [
          {
            icon: <EyeOutlined />,
            action: (record) => {
              navigate(`/administration/users/${record?.id}`);
            },
            security: {
              permissions: administration.users.view,
            },
          },
        ]}
      />
    </>
  );
};
